import React from 'react';
import { Formik, Form } from 'formik';
import axios from 'axios';
import * as yup from 'yup';
import { useNavigate, NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { Button, Grid, Stack, Typography } from '@mui/material';
import { Box } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import ButtonLoading from '../../Controls/Buttons/ButtonLoading';
import Copyright from '../../Apm/Other/Copyright';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import { baseURL } from '../../Data/baseURL';
import passReset from '../../Data/MetaData/passReset.json';
import MetaDecorator from '../../Apm/Other/MetaDecorator';
import { ContDePlataLogo_onwhite1 } from '../../Data/imageURL';

import AuthShell from '../../Public/AuthShell';

const initialValues = {
  email: '',
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email(() => i18n.t('yup.invalid_email'))
    .required(() => i18n.t('yup.invalid_email')),
});

const axiosInst = axios.create({
  baseURL: baseURL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

export default function PassReset() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = (e, values) => {
    e.preventDefault();

    setLoading(true);
    values = {
      email: values.email.trim().toLowerCase(),
    };

    axiosInst
      .post('/auth/users/reset_password/', {
        email: values.email,
      })
      .then((res) => {
        res.status === 204 && navigate('/', { replace: true });
        res.status === 204
          ? toast.success(() => t('toast.check_email_address_to_confirm_new_password'))
          : toast.error(() => t('toast.not_successful_try_again'), {
              autoClose: 4000,
            });
      })
      .catch(() => {
        toast.error(() => t('toast.check_email_address_and_try_again'), {
          autoClose: 4000,
        });
        setLoading(false);
      });
  };

  return (
    <>
      <MetaDecorator
        description={passReset.pageDescriptionRo}
        title={passReset.pageTitle}
        imageUrl={ContDePlataLogo_onwhite1}
        imageAlt={passReset.pageDescriptionRo}
        rel="/apps/passreset"
      />
      <AuthShell mode="reset">
        <Button component={NavLink} to="/" color="primary" size="small" variant="text">
          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Box sx={{ alignItems: 'flex-end' }}>
              <KeyboardBackspaceIcon />
            </Box>
            <Typography variant="button">{t('button.back_to_main_page')}</Typography>
          </Stack>
        </Button>

        <Typography align="center" variant="h5" sx={{ mt: 10, mb: 2 }}>
          {t('button.forgot_password')}
        </Typography>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values }) => {
            return (
              <Form>
                <TextFieldNormal
                  variant="outlined"
                  required
                  fullWidth
                  id="email"
                  label={t('general.enter_your_email')}
                  name="email"
                  autoComplete="email"
                  // autoFocus
                />
                <ButtonLoading
                  fullWidth
                  loading={loading}
                  variant="contained"
                  color="primary"
                  onClick={(e) => handleSubmit(e, values)}
                  sx={{ mt: 4 }}
                >
                  {t('button.send_request')}
                </ButtonLoading>
                <Grid container sx={{ mt: 5 }} spacing={1}>
                  <Grid item xs={12} sx={{ mb: 1 }}>
                    <Button component={NavLink} to="/apps/login" color="primary" size="small" variant="text">
                      <Typography variant="button">{t('button.already_have_account')}</Typography>
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Button component={NavLink} to="/apps/signup" color="primary" size="small" variant="text">
                      <Typography variant="button">{t('button.do_not_have_account')}</Typography>
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>

        <Box sx={{ mt: 6, mb: 5 }}>
          <Copyright />
        </Box>
      </AuthShell>
    </>
  );
}
